import { Box, ClickAwayListener, Collapse } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { CaretDownIcon, MemberDownIcon } from 'assets/icons'
import Avatar from 'components/badges/Avatar'
import { GroupCardAvatarContainer } from 'components/cards/styled'
import Popup from 'components/popup/Popup'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useProgress } from 'components/providers/ProgressProvider'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { removePendingParticipantFromGroup } from 'services/api/group'
import {
  localGroupById,
  ParticipantsForGroup
} from 'services/db/Queries/GroupQueries'
import Logger from 'services/Logger'
import { isUserAdminInGroup } from 'utils/api/groups'
import { getRandomColor } from 'utils/colors'
import { isCypress } from 'utils/cypress'

import ObserveService from '../../services/db/Observe'
import LocalStorage from '../../services/LocalStorage'
import {
  EventDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantStatus,
  GroupParticipantType,
  ParticipantDataDoc,
  ProfileDoc
} from '../../types/api/changes'
import { Model } from '../../types/model'
import { LocalStorageKey } from '../../types/services/localStorage'
import { ParticipantsActionsPopupContent } from '../popups/groupInfo/participantActions/ParticipantsActionPopup'
import * as Styled from './styled'

export const MEMBERS_THRESHOLD = 3

interface MemberMenu {
  anchorEl: HTMLElement | null
  participant: ParticipantDataDoc
}

export function MembersList(props: MemberListProps) {
  Logger.orange('MembersList render', props.participants)
  const progress = useProgress()
  const [showExpander, setShowExpander] = useState(
    props.noExpander ? false : true
  )
  const [participants, setParticipants] = useState<
    Record<string, GroupParticipant>
  >(props.group?.Participants ?? props.participants ?? {})
  const [membersGroup, setMembersGroup] = useState<
    Model<GroupDoc> | undefined | GroupDoc
  >(props.group)

  const [participantsData, setParticipantsData] = useState(
    props.localParticipants ?? []
  )
  const participantsAmount = Object.values(participants).filter(
    (participant) => participant.Status === GroupParticipantStatus.ACTIVE
  ).length

  const pendingParticipantsAmount = Object.values(
    props.group?.PendingParticipants ?? {}
  ).filter(
    (participant) => participant.Status === GroupParticipantStatus.ACTIVE
  ).length

  const overallAmount = participantsAmount + pendingParticipantsAmount

  let pendingSlice = undefined

  if (participantsAmount < MEMBERS_THRESHOLD && showExpander) {
    pendingSlice = MEMBERS_THRESHOLD - participantsAmount
  }

  const showAllMembers = () => {
    setShowExpander(false)
  }
  const sortedParticipants = participantsData.sort((a, b) => {
    const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)

    const aIsOwner = a.AccountID === membersGroup?.OwnerID
    const bIsOwner = b.AccountID === membersGroup?.OwnerID

    const aIsAdmin = isUserAdminInGroup(membersGroup, a.AccountID)
    const bIsAdmin = isUserAdminInGroup(membersGroup, b.AccountID)

    const aIsYou = a.AccountID === currentUserId
    const bIsYou = b.AccountID === currentUserId

    // Owners first
    if (aIsOwner && !bIsOwner) return -1
    if (!aIsOwner && bIsOwner) return 1

    // Then admins
    if (aIsAdmin && !bIsAdmin) return -1
    if (!aIsAdmin && bIsAdmin) return 1

    // Then "You"
    if (aIsYou && !bIsYou) return -1
    if (!aIsYou && bIsYou) return 1

    // Others stay in original order
    return 0
  })

  const participantsToDraw = sortedParticipants.slice(0, MEMBERS_THRESHOLD)
  const additionalParticipants = sortedParticipants.slice(MEMBERS_THRESHOLD)
  const activeMemberIndexes = new Map(
    sortedParticipants
      .filter(
        (participant) =>
          participants[participant.AccountID]?.Status ===
          GroupParticipantStatus.ACTIVE
      )
      .map((participant, index) => [participant.AccountID, index + 1])
  )

  useEffect(() => {
    Logger.blue('MembersList useEffect groupId/group', {
      groupId: props.groupId,
      groupRev: props.group?._rev
    })
    if (props.groupId)
      localGroupById(props.groupId).then((group) => {
        setParticipants(group?.Participants ?? {})
        setMembersGroup(group)
        ParticipantsForGroup(Object.keys(group?.Participants ?? {}))
          .fetch()
          .then((participants) => {
            setParticipantsData(participants)
          })
        return group
      })
  }, [props.groupId, props.group?._rev])

  useEffect(() => {
    if (props.addedMemberPhone) {
      setShowExpander(false)
      setTimeout(() => {
        let element = isCypress()
          ? document.querySelector<HTMLElement>(
              `[data-member-phone="${props.addedMemberPhone}"]`
            )
          : document.getElementById(`member-row-${props.addedMemberPhone}`)

        if (!element) {
          element = document.getElementById(
            `pending-member-${props.addedMemberPhone}`
          )
        }

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 500)
    }
  }, [props.addedMemberPhone])

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | MemberMenu>(null)

  const activePendingParticipantPhones = useMemo(() => {
    return Object.entries(props.group?.PendingParticipants ?? {}).reduce(
      (acc: string[], [phone, participant]) => {
        if (participant.Status === GroupParticipantStatus.ACTIVE) {
          acc.push(phone)
        }
        return acc
      },
      []
    )
  }, [props.group?.PendingParticipants])

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        setMenuAnchorEl(null)
      }}
    >
      <span>
        <Styled.MembersList id="membersList">
          {participantsToDraw.map((participant) => {
            if (
              participants[participant.AccountID] &&
              participants[participant.AccountID].Status !==
                GroupParticipantStatus.ACTIVE
            )
              return <></>

            return (
              <div
                key={participant.AccountID}
                style={{ width: '100%', marginTop: '4px', marginBottom: '4px' }}
              >
                <MemberListRow
                  eventHook={props.eventHook}
                  actionsOnEvent={props.actionsOnEvent}
                  profile={props.profile}
                  participant={participant}
                  group={membersGroup}
                  event={props.event}
                  setLoading={props.setLoading}
                  withoutActions={props.withoutActions}
                  menuAnchorEl={menuAnchorEl}
                  setMenuAnchorEl={setMenuAnchorEl}
                  participants={participants}
                  progress={progress}
                  memberIndex={activeMemberIndexes.get(participant.AccountID)}
                />
              </div>
            )
          })}

          <Collapse in={!showExpander} timeout={300}>
            <div>
              {additionalParticipants.map((participant) => {
                return (
                  <div
                    key={participant.AccountID}
                    style={{
                      width: '100%',
                      marginTop: '4px',
                      marginBottom: '4px'
                    }}
                  >
                    <MemberListRow
                      eventHook={props.eventHook}
                      actionsOnEvent={props.actionsOnEvent}
                      profile={props.profile}
                      participant={participant}
                      group={membersGroup}
                      event={props.event}
                      setLoading={props.setLoading}
                      withoutActions={props.withoutActions}
                      menuAnchorEl={menuAnchorEl}
                      setMenuAnchorEl={setMenuAnchorEl}
                      participants={participants}
                      progress={progress}
                      memberIndex={activeMemberIndexes.get(
                        participant.AccountID
                      )}
                    />
                  </div>
                )
              })}
              {activePendingParticipantPhones
                .slice(0, pendingSlice)
                .map((phoneNumber) => {
                  return (
                    <div key={phoneNumber} id={`member-row-${phoneNumber}`}>
                      <PendingMember
                        phoneNumber={phoneNumber}
                        participant={
                          props.group?.PendingParticipants?.[phoneNumber]!
                        }
                        group={membersGroup!}
                      />
                    </div>
                  )
                })}
            </div>
          </Collapse>

          <Collapse
            in={overallAmount > MEMBERS_THRESHOLD && showExpander}
            timeout={300}
          >
            <div onClick={showAllMembers} style={{ cursor: 'pointer' }}>
              <Styled.MemberListItem>
                <Styled.MembersListNameContainer columnGap={1}>
                  <span
                    style={{
                      marginLeft: i18n.dir() === 'ltr' ? 4 : 0,
                      marginRight: i18n.dir() === 'ltr' ? 0 : 4,
                      fontFamily: 'NotoSans',
                      fontSize: '14px'
                    }}
                  >
                    {t('membersAmount', {
                      members: overallAmount - MEMBERS_THRESHOLD
                    })}
                  </span>
                  <CaretDownIcon width={10} />
                </Styled.MembersListNameContainer>
              </Styled.MemberListItem>
            </div>
          </Collapse>
        </Styled.MembersList>
      </span>
    </ClickAwayListener>
  )
}

interface PendingMemberProps {
  phoneNumber: string
  participant: GroupParticipant
  group: Model<GroupDoc> | GroupDoc
}

function PendingMember(props: PendingMemberProps) {
  const { t } = useTranslation()
  const progress = useProgress()
  const [isOpen, setIsOpen] = useState(false)
  const color = useMemo(
    () => getRandomColor(Number(props.phoneNumber)),
    [Number(props.phoneNumber)]
  )

  const onRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsOpen(true)
  }

  const handleRemove = async () => {
    progress.setLoading(true)
    await removePendingParticipantFromGroup(props.phoneNumber, props.group)
    progress.setLoading(false)
    setIsOpen(false)
  }

  const adminOrOwner =
    isUserAdminInGroup(
      props.group,
      LocalStorage.get(LocalStorageKey.USER_ID)!
    ) || props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)

  return (
    <Styled.MemberListItem
      key={props.phoneNumber}
      onContextMenu={onRightClick}
      onClick={onRightClick}
      gap={0.5}
      canMakeActions={adminOrOwner}
      mt={'4px'}
      mb={'4px'}
    >
      <Styled.MembersListNameContainer>
        <GroupCardAvatarContainer width={30} height={30} color={'transparent'}>
          <Avatar
            img={''}
            size="sm"
            color={color}
            name={props.phoneNumber.substring(1, 2)}
          />
        </GroupCardAvatarContainer>
        <Popup
          open={isOpen && adminOrOwner}
          setOpen={setIsOpen}
          closeOnTouchOutside
          content={
            <Box p={1}>
              <PopupSectionRow
                onlyTitle
                title={t('removeFromSharedCalendar')}
                action={handleRemove}
                clickableTitle
              />
            </Box>
          }
        >
          <span style={{ fontSize: '14px' }}>{props.phoneNumber}</span>
        </Popup>
      </Styled.MembersListNameContainer>
      <span data-content="pending">{t('invitedToGroupcal')}</span>
      <MemberDownIcon className="hover-arrow" width={15} />
    </Styled.MemberListItem>
  )
}

interface MemberListRowProps {
  event?: Model<EventDoc> | EventDoc
  participant: Model<ParticipantDataDoc>
  setLoading: (b: boolean) => void
  group?: Model<GroupDoc> | GroupDoc
  withoutActions: boolean
  profile?: Model<ProfileDoc>
  actionsOnEvent?: boolean
  eventHook?: IEventDetails
  menuAnchorEl: MemberMenu | null
  setMenuAnchorEl: (el: MemberMenu | null) => void
  participants: Record<string, GroupParticipant>
  progress: any
  memberIndex?: number
}

function MemberListRow(props: MemberListRowProps) {
  const userName =
    props.participant.AccountID ==
    (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
      ? t('you')
      : props.participant.FullName != 'null'
        ? props.participant.FullName
        : (props.participant.PhoneNumber ?? t('tapToUpdate'))

  const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)!

  const canMakeActions =
    (props.group?.Participants?.[currentUserId]?.Type ===
      GroupParticipantType.ADMIN ||
      currentUserId === props.group?.OwnerID) &&
    !props.withoutActions

  const onMemberClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    event.preventDefault()
    if (canMakeActions) {
      // Find the username span element to use as anchor
      const usernameSpan = event.currentTarget.querySelector(
        `#user-name-${props.participant.AccountID}`
      ) as HTMLElement

      if (
        props.menuAnchorEl?.participant.AccountID ===
        props.participant.AccountID
      ) {
        props.setMenuAnchorEl(null)
        return
      }

      if (usernameSpan) {
        props.setMenuAnchorEl?.({
          anchorEl: usernameSpan,
          participant: props.participant
        })
      }
    }
  }

  const onMemberRightClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    event.preventDefault()
    if (canMakeActions) {
      // Find the username span element to use as anchor
      const usernameSpan = event.currentTarget.querySelector(
        `#user-name-${props.participant.AccountID}`
      ) as HTMLElement

      if (usernameSpan) {
        props.setMenuAnchorEl?.({
          anchorEl: usernameSpan,
          participant: props.participant
        })
      }
    }
  }

  const color = useMemo(
    () => getRandomColor(Number(props.participant.PhoneNumber)),
    [Number(props.participant.PhoneNumber)]
  )

  const participantColor =
    props.group?.Participants[props.participant.AccountID]?.MemberColor

  let statusText = t('pending')
  let statusColor = 'var(--color-text-muted)'

  if (props.event) {
    const deliveryStatus =
      props.event.ParticipantsStatus?.[props.participant.AccountID]
        ?.ParticipantDeliveryStatus

    statusText = deliveryStatus === '9' ? t('received') : t('sent')

    if (props.event.RequestConfirmation === '1') {
      const confirmStatus =
        props.event.ParticipantsStatus?.[props.participant.AccountID]
          ?.ParticipantConfirmationStatus

      switch (confirmStatus) {
        case '2':
          statusText = t('accepted')
          statusColor = '#008000'
          break
        case '4':
          statusText = t('maybe')
          statusColor = '#D4C91D'
          break
        case '3':
          statusText = t('declined')
          statusColor = 'var(--color-negative)'
          break
        case '1':
          statusColor = 'var(--color-text-muted)'
          break
      }
    }
  }

  if (!props.group?.Participants[props.participant.AccountID]) return <></>

  if (!props.event || props.event?._id?.length === 0) statusText = ''

  return (
    <span
      id={
        isCypress() && props.memberIndex
          ? `member-row-user${props.memberIndex}`
          : `member-row-${props.participant.PhoneNumber}`
      }
      data-member-phone={
        isCypress() ? props.participant.PhoneNumber : undefined
      }
      onClick={onMemberClick}
      onContextMenu={onMemberRightClick}
      style={{ cursor: canMakeActions ? 'pointer' : 'default' }}
    >
      {props.group?.Participants[props.participant.AccountID].Status ===
      GroupParticipantStatus.ACTIVE ? (
        <Styled.MemberListItem
          key={props.participant.AccountID}
          canMakeActions={canMakeActions}
        >
          <Styled.MembersListNameContainer>
            <GroupCardAvatarContainer
              borderWidth="2px"
              width={30}
              height={30}
              color={participantColor ? participantColor : 'transparent'}
            >
              <Avatar
                img={
                  props.participant.AccountID ===
                  LocalStorage.get(LocalStorageKey.USER_ID)
                    ? props.profile?.ProfilePicture
                    : props.participant.PhotoURL
                }
                size="sm"
                color={color}
                name={userName}
              />
            </GroupCardAvatarContainer>
            <Box display={'flex'} flexDirection={'column'}>
              <Popup
                setOpen={(b: boolean) => {
                  if (!b) props.setMenuAnchorEl(null)
                }}
                open={
                  props.menuAnchorEl?.participant.AccountID ===
                  props.participant.AccountID
                }
                content={
                  <ParticipantsActionsPopupContent
                    eventHook={props.eventHook}
                    actionsOnEvent={props.actionsOnEvent}
                    setError={props.progress.setError}
                    group={props.group!}
                    setLoading={props.setLoading}
                    setIsOpen={(open) => {
                      if (!open) props.setMenuAnchorEl?.(null)
                    }}
                    participant={
                      props.participants[props.participant.AccountID]
                    }
                    participantId={props.participant.AccountID}
                    participantInfo={props.participant}
                  />
                }
              >
                <span
                  id={`user-name-${props.participant.AccountID}`}
                  style={{
                    cursor: canMakeActions ? 'pointer' : 'default',

                    fontSize: '14px'
                  }}
                >
                  {userName}
                </span>
              </Popup>
              {
                <span
                  style={{
                    fontSize: '10px',
                    color: statusColor
                  }}
                >
                  {`${statusText}`}
                </span>
              }
            </Box>
          </Styled.MembersListNameContainer>
          <span data-content="role">
            {props.participant.AccountID ===
            (props.actionsOnEvent
              ? props.eventHook?.getValues('OwnerID')
              : props.group?.OwnerID)
              ? props.event
                ? t('eventOwner')
                : t('owner')
              : props.group.Participants[props.participant.AccountID]?.Type ==
                  '2'
                ? t('admin')
                : ''}
            {canMakeActions && (
              <MemberDownIcon className="hover-arrow" width={15} />
            )}
          </span>
        </Styled.MemberListItem>
      ) : (
        <></>
      )}
    </span>
  )
}

interface MemberListProps {
  event?: Model<EventDoc> | EventDoc
  groupId?: string
  localParticipants: Model<ParticipantDataDoc>[]
  setLoading: (b: boolean) => void
  group?: Model<GroupDoc> | GroupDoc
  withoutActions: boolean
  participants?: Record<string, GroupParticipant>
  pending?: boolean
  pendingParticipants?: Record<string, GroupParticipant>
  profile?: Model<ProfileDoc>
  eventHook?: IEventDetails
  actionsOnEvent?: boolean
  addedMemberPhone?: string
  noExpander?: boolean
}

export default withObservables(['group'], (props: MemberListProps) => ({
  localParticipants: ObserveService.ParticipantsForIds(
    Object.keys(props.participants ?? props.group?.Participants ?? {})
  ),
  profile: ObserveService.Profile()
}))(MembersList)
